import { useEffect, useState, useContext, useCallback } from "react";
import DataTable from "../../utils/DataTable";
import SkeletonLoading from "../../utils/Loading/SkeletonLoading";
import ShopContext from "../../../context/ShopContext";
import ShopsHelper from "../../../helpers/ShopsHelper";
import styles from "./styles.module.css";
import EmptyState from "../../utils/EmptyState/EmptyState";
import DateTimeHelpers from "../../../helpers/DateTimeHelpers";
import * as ReportHelper from "helpers/ReportHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import Button from "@mui/material/Button";
import PrinterImg from "images/svg/printer.svg";
import { INVOICE_FORMATS } from "const";

export default function ItemisedBillSummaryReport({ shiftId }) {
  const { shop } = useContext(ShopContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const rows = await ReportHelper.getItemisedRowsForShift(shiftId);

      setRows(rows || []);
    } catch (error) {
      console.error("Failed to fetch Itemised Bill Summary:", error);
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const headerData = [
    {
      label: "Bill No",
      id: "bill_no",
      type: "callback",
      viewRender: (obj) => obj.bill_no || obj.fid,
    },
    {
      label: "PLU",
      id: "plu",
      type: "text",
    },
    {
      label: "Name",
      id: "name",
      type: "text",
      width: "280px",
    },
    {
      label: "Date",
      id: "date",
      type: "text",
      width: "110px",
    },
    {
      label: "Time",
      id: "time",
      type: "callback",
      width: "100px",
      viewRender: (item) => DateTimeHelpers.formatServerTimeSegment(item.time),
    },
    {
      label: "Sold Quantity",
      id: "quantity",
      type: "callback",
      viewRender: (item) => {
        if (!item.quantity || isNaN(item.quantity)) return;
        return `${ShopsHelper.getAmountFormatted(shop, item.quantity)} ${item.unit || ""}`;
      },
    },
    {
      label: "Unit Price",
      id: "price",
      type: "floatAmount",
    },
    {
      label: "Tax(%)",
      id: "taxPercentage",
      type: "floatAmount",
    },
    {
      label: "Tax",
      id: "tax_amount",
      type: "floatAmount",
    },
    {
      label: "Additional Tax(%)",
      id: "cessPercentage",
      type: "floatAmount",
    },
    {
      label: "Additional Tax",
      id: "cess_amount",
      type: "floatAmount",
    },

    {
      label: "Total Amount",
      id: "payable",
      type: "floatAmount",
    },
  ];

  const handlePrint = async () => {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;

    let reportText;
    if (
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC ||
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE
    ) {
      reportText = await ReportHelper.getItemisedSummaryMultiThreeInch(rows, shop, shiftId);
    } else {
      reportText = await ReportHelper.getItemisedSummaryReport(rows, shop, shiftId);
    }
    await ReportHelper.printReport({ reportText, fallbackFunction: window.print });
  };

  const _getItemisedReportSummary = (items) => {
    let totalPayable = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;

    (items || []).forEach((item) => {
      totalPayable += Number(item?.payable || 0);
      totalTax += Number(item.tax_amount || 0);
      totalAdditionalTax += Number(item.cess_amount || 0);
    });

    const grossTotal = items?.summary?.grossTotal ?? totalPayable;
    const totalDiscount = items?.summary?.totalDiscount ?? 0;
    const netTotal = items?.summary?.netTotal ?? Math.max(0, grossTotal - totalDiscount);

    return {
      grossTotal: ShopsHelper.getAmountFormatted(shop, grossTotal),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
      rawDiscount: totalDiscount,
      netTotal: ShopsHelper.getAmountFormatted(shop, netTotal),
      totalPayable: ShopsHelper.getAmountFormatted(shop, totalPayable),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
    };
  };

  const summary = _getItemisedReportSummary(rows);

  return (
    <div className={styles.container}>
      <div style={{ height: "100%" }}>
        <div className={styles.headerSection}>
          <div>
            <h2 className={styles.title}>Itemised Sales Summary</h2>
            <h3 className={styles.subTitle}>Key financials and status of the current shift</h3>
          </div>

          <Button
            className={`${styles.button} ${styles.printBtn}`}
            onClick={handlePrint}
            startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
          >
            Print
          </Button>
        </div>
        {loading ? (
          <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
        ) : rows.length > 0 ? (
          <>
            <DataTable
              columns={headerData}
              rows={rows}
              rowKey="id"
              summary={{
                bill_no: "Total",
                tax_amount: `${shop?.currency || ""} ${summary.totalTax}`,
                cess_amount: `${shop?.currency || ""} ${summary.totalAdditionalTax}`,
                payable: `${shop?.currency || ""} ${summary.totalPayable}`,
              }}
            />
            <div className={styles.summaryCardContainer}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryCardRow}>
                  <span className={styles.summaryCardLabel}>Item Total (Gross)</span>
                  <span className={styles.summaryCardColon}>:</span>
                  <span className={styles.summaryCardValue}>
                    {shop?.currency || ""} {summary.grossTotal}
                  </span>
                </div>
                {Number(summary.rawDiscount || 0) > 0 && (
                  <div className={styles.summaryCardRow}>
                    <span className={styles.summaryCardLabel}>Total Discount</span>
                    <span className={styles.summaryCardColon}>:</span>
                    <span className={styles.summaryCardDiscount}>
                      - {shop?.currency || ""} {summary.totalDiscount}
                    </span>
                  </div>
                )}
                <div className={`${styles.summaryCardRow} ${styles.summaryCardTotalRow}`}>
                  <span className={styles.summaryCardTotalLabel}>Net Total</span>
                  <span className={styles.summaryCardColon}>:</span>
                  <span className={styles.summaryCardTotalValue}>
                    {shop?.currency || ""} {summary.netTotal}
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <EmptyState title="Itemised Bill Summary" />
        )}
      </div>
    </div>
  );
}
